import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ApprovalAction, PendingApproval, ApprovalRequestDetail } from '@/lib/api/types'
import { CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ApprovalActionsPanelProps {
  approval: PendingApproval | ApprovalRequestDetail
  onProcess: (action: ApprovalAction, comments?: string) => Promise<void>
  processing?: boolean
  className?: string
}

export function ApprovalActionsPanel({
  approval,
  onProcess,
  processing,
  className,
}: ApprovalActionsPanelProps) {
  const [confirmAction, setConfirmAction] = useState<ApprovalAction | null>(null)
  const [comments, setComments] = useState('')

  const canApprove = approval.canApprove
  const canReject = approval.canReject
  const canRequestChanges = approval.canRequestChanges

  const handleAction = (action: ApprovalAction) => {
    if (action === 'Approve') {
      // Approve can be done without confirmation
      onProcess('Approve')
    } else {
      // Reject and Request Changes require confirmation/comments
      setConfirmAction(action)
      setComments('')
    }
  }

  const handleConfirm = async () => {
    if (!confirmAction) return
    await onProcess(confirmAction, comments.trim() || undefined)
    setConfirmAction(null)
    setComments('')
  }

  const getActionInfo = (action: ApprovalAction) => {
    switch (action) {
      case 'Approve':
        return {
          icon: <CheckCircle2 className="h-5 w-5" />,
          label: 'Approve',
          description: 'This template will move to the next stage or be approved for publishing.',
          buttonClass:
            'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700',
          dialogTitle: 'Approve Template',
        }
      case 'Reject':
        return {
          icon: <XCircle className="h-5 w-5" />,
          label: 'Reject',
          description:
            'The template will be rejected and the submitter will be notified. Please provide a reason below.',
          buttonClass:
            'bg-red-600 hover:bg-red-700 text-white dark:bg-red-600 dark:hover:bg-red-700',
          dialogTitle: 'Reject Template',
        }
      case 'RequestChanges':
        return {
          icon: <AlertCircle className="h-5 w-5" />,
          label: 'Request Changes',
          description:
            'The template will be sent back to the submitter for modifications. Please describe the required changes below.',
          buttonClass:
            'bg-orange-600 hover:bg-orange-700 text-white dark:bg-orange-600 dark:hover:bg-orange-700',
          dialogTitle: 'Request Changes',
        }
    }
  }

  if (!canApprove && !canReject && !canRequestChanges) {
    return null
  }

  return (
    <>
      <div className={cn('space-y-3', className)}>
        <Label className="text-sm font-medium">Actions</Label>
        <div className="flex flex-wrap gap-2">
          {canApprove && (
            <Button
              onClick={() => handleAction('Approve')}
              disabled={processing}
              className={cn(getActionInfo('Approve').buttonClass)}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Approve
            </Button>
          )}

          {canRequestChanges && (
            <Button
              onClick={() => handleAction('RequestChanges')}
              disabled={processing}
              className={cn(getActionInfo('RequestChanges').buttonClass)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Request Changes
            </Button>
          )}

          {canReject && (
            <Button
              onClick={() => handleAction('Reject')}
              disabled={processing}
              className={cn(getActionInfo('Reject').buttonClass)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent>
          {confirmAction && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {getActionInfo(confirmAction).icon}
                  {getActionInfo(confirmAction).dialogTitle}
                </DialogTitle>
                <DialogDescription>
                  {getActionInfo(confirmAction).description}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="comments">
                    {confirmAction === 'Reject' ? 'Reason for rejection' : 'Required changes'}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder={
                      confirmAction === 'Reject'
                        ? 'Please explain why this template is being rejected...'
                        : 'Describe the changes that need to be made...'
                    }
                    rows={4}
                    className="resize-none"
                  />
                  {confirmAction !== 'Approve' && !comments.trim() && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      A comment is required for this action
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmAction(null)}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={processing || (confirmAction !== 'Approve' && !comments.trim())}
                  className={cn(getActionInfo(confirmAction).buttonClass)}
                >
                  {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {confirmAction === 'Reject'
                    ? 'Reject Template'
                    : confirmAction === 'RequestChanges'
                    ? 'Request Changes'
                    : 'Confirm'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
